import { useEffect, useState } from 'react';
import { Alert, Link, Loading, Button } from '@elementary/common-ui/components';
import useNango from 'shared/hooks/useNango';
import useSupportChat from 'shared/hooks/useSupportChat';
import { FetchPagerDutyApi } from 'shared/types';
import { IntegrationError } from 'settings/types';
import EditConfirmationDialog from '../EditConfirmationDialog';
import IntegrationDialog from '../../../shared/Integration/IntegrationDialog';
import TestIntegrationSection from '../IntegrationTestSections/TestIntegrationSection';
import PagerDutyTestSection from '../IntegrationTestSections/PagerDutyTestSection';

enum DialogStep {
  initial,
  auth,
  success,
  failure,
}

type Props = {
  open: boolean;
  onClose: () => void;
  data?: FetchPagerDutyApi;
  onPagerDutyIntegrationSubmit: () => void;
  isPagerDutyIntegrationLoading?: boolean;
  error?: IntegrationError;
  edit?: boolean;
  onDisconnect?: () => void;
};

const PagerDutyDialog = ({
  open,
  onClose,
  data,
  onPagerDutyIntegrationSubmit,
  isPagerDutyIntegrationLoading,
  error,
  edit = false,
  onDisconnect,
}: Props) => {
  const [step, setStep] = useState<DialogStep>(DialogStep.initial);
  const [showEditConfirmationDialog, setShowEditConfirmationDialog] =
    useState(false);
  const [testSuccess, setTestSuccess] = useState(false);
  const { nangoAuth, success: nangoSuccess, error: nangoError } = useNango();
  const supportChat = useSupportChat();

  useEffect(() => {
    if (data?.integrated) {
      setStep(DialogStep.success);
    } else {
      setStep(DialogStep.auth);
    }
  }, [data]);

  useEffect(() => {
    if (step === DialogStep.auth) {
      nangoAuth('pagerduty');
    }
  }, [step, nangoAuth]);

  // If Nango auth succeeded, we create integration in the DB and move to step success.
  useEffect(() => {
    if (nangoSuccess) {
      onPagerDutyIntegrationSubmit();
      setStep(DialogStep.success);
    }
  }, [nangoSuccess, onPagerDutyIntegrationSubmit, setStep]);

  // If Nango auth failed, we move to step failure.
  useEffect(() => {
    if (nangoError) {
      setStep(DialogStep.failure);
    }
  }, [nangoError, setStep]);

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title="Connect PagerDuty"
      hideSaveButton
      error={error}
    >
      {step === DialogStep.initial && <Loading />}

      {step === DialogStep.auth && <Loading />}

      {isPagerDutyIntegrationLoading && <Loading />}

      {step === DialogStep.success && !edit && (
        <Alert severity="success" style={{ fontWeight: 'bold' }}>
          Elementary PagerDuty app was successfully connected
        </Alert>
      )}

      {step === DialogStep.success && edit && !isPagerDutyIntegrationLoading && (
        <Button
          onClick={() => {
            setShowEditConfirmationDialog(true);
          }}
        >
          Re-authenticate your PagerDuty integration
        </Button>
      )}

      {step === DialogStep.failure && (
        <Alert severity="error" style={{ fontWeight: 'bold' }}>
          Elementary PagerDuty app failed to connect, please{' '}
          <Link onClick={supportChat.show} href="#">
            contact us
          </Link>{' '}
          for further assistance.
        </Alert>
      )}

      <TestIntegrationSection isSuccess={testSuccess}>
        <PagerDutyTestSection
          onSuccess={() => setTestSuccess(true)}
          onReset={() => setTestSuccess(false)}
        />
      </TestIntegrationSection>

      <EditConfirmationDialog
        open={showEditConfirmationDialog}
        onClose={() => setShowEditConfirmationDialog(false)}
        onConfirm={() => {
          setShowEditConfirmationDialog(false);
          setStep(DialogStep.initial);
          nangoAuth('pagerduty');
        }}
        message="This will overwrite the current PagerDuty authentication details. Make sure the new user has admin permissions to avoid integration issues."
      />
      {edit && onDisconnect && (
        <Button
          variant="text"
          color="error"
          onClick={onDisconnect}
          sx={{ alignSelf: 'flex-start', mt: 1 }}
        >
          Disconnect PagerDuty
        </Button>
      )}
    </IntegrationDialog>
  );
};

export default PagerDutyDialog;
